import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import CustomSelect from "../components/common/CustomSelect"; // Import CustomSelect
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import {
  Trash2,
  Edit,
  Save,
  Undo,
  Redo,
  ChevronUp,
  ChevronDown as ChevronDownMove,
  Navigation,
  ArrowLeft,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import LoadingComponent from "../components/ui/LoadingComponent";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import { htmlComponents } from "./workflow/HtmlComponent";

// Field Configuration Modal
const FieldConfigModal = ({
  isOpen,
  onClose,
  field,
  onSave,
  isEditMode,
  fieldNames,
  optionSources,
}) => {
  const [config, setConfig] = useState({
    name: "",
    label: "",
    required: false,
    validation: "",
    min: "",
    max: "",
    type: "",
    options: "",
    options_source: null,
    options_label_field: null,
    options_value_field: null,
    order: 1,
  });
  const [optionSourceType, setOptionSourceType] = useState("");
  const [selectedApiSource, setSelectedApiSource] = useState(null); // Changed to null for consistency
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  useEffect(() => {
    if (field) {
      let min = "";
      let max = "";
      if (
        ["text", "number", "textarea"].includes(field.type) &&
        field.validation
      ) {
        const validationParts = field.validation.split("|");
        validationParts.forEach((part) => {
          if (part.startsWith("min:")) min = part.split(":")[1];
          if (part.startsWith("max:")) max = part.split(":")[1];
        });
      }
      setConfig({
        name: field.name || "",
        label: field.label || "",
        required: field.required || false,
        validation: field.validation || "",
        min: min,
        max: max,
        type: field.type || "",
        options: Array.isArray(field.options)
          ? field.options.join("|")
          : field.options || "",
        options_source: field.options_source || null,
        options_label_field: field.options_label_field || null,
        options_value_field: field.options_value_field || null,
        order: field.order || 1,
      });
      setOptionSourceType(
        field.options_source
          ? "api"
          : field.options && field.options.length > 0
          ? "hard_coded"
          : ""
      );
      // Find the full API source object from optionSources.api
      const apiSource = field.options_source
        ? optionSources.api.find(
            (source) => source.url === field.options_source
          )
        : null;
      setSelectedApiSource(apiSource || null);
    }
  }, [field, optionSources]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSourceTypeChange = (selectedOption) => {
    const type = selectedOption?.value;
    setOptionSourceType(type);
    setConfig((prev) => ({
      ...prev,
      options: "",
      options_source: null,
      options_label_field: null,
      options_value_field: null,
    }));
    setSelectedApiSource(null); // Clear selected API source
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!config.label.trim()) {
      handleCustomResponse("error", "Field Label cannot be empty.");
      return;
    }
    if (!config.name) {
      handleCustomResponse("error", "Please select a Field Name.");
      return;
    }
    if (config.type === "select") {
      if (
        optionSourceType === "hard_coded" &&
        (!config.options || config.options.trim() === "")
      ) {
        handleCustomResponse(
          "error",
          "Please add at least one option for hardcoded dropdown."
        );
        return;
      }
      if (optionSourceType === "api" && !selectedApiSource) {
        handleCustomResponse(
          "error",
          "Please select an API source for the dropdown."
        );
        return;
      }
    }
    if (["text", "number", "textarea"].includes(config.type)) {
      if (config.min && isNaN(config.min)) {
        handleCustomResponse("error", "Minimum value must be a number.");
        return;
      }
      if (config.max && isNaN(config.max)) {
        handleCustomResponse("error", "Maximum value must be a number.");
        return;
      }
      if (
        config.min &&
        config.max &&
        parseFloat(config.min) > parseFloat(config.max)
      ) {
        handleCustomResponse(
          "error",
          "Minimum value cannot be greater than maximum value."
        );
        return;
      }
    }

    const validation = ["text", "number", "textarea"].includes(config.type)
      ? [config.min && `min:${config.min}`, config.max && `max:${config.max}`]
          .filter(Boolean)
          .join("|")
      : config.validation;

    const fieldToSave = {
      ...field,
      ...config,
      validation,
      options:
        config.type === "select" &&
        optionSourceType === "hard_coded" &&
        config.options
          ? config.options.split("|").map((opt) => opt.trim())
          : null,
      options_source:
        optionSourceType === "api" ? selectedApiSource?.url : null,
      options_label_field:
        optionSourceType === "api" ? selectedApiSource?.label_field : null,
      options_value_field:
        optionSourceType === "api" ? selectedApiSource?.value_field : null,
    };

    onSave(fieldToSave);
    onClose();
  };

  if (!isOpen) return null;

  const fieldNameOptions = fieldNames.map((field) => ({
    value: field.field_name,
    label: field.field_name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
  }));

  const sourceTypeOptions = [
    { value: "hard_coded", label: "Write By Own" },
    { value: "api", label: "Get By API" },
  ];

  const apiSourceOptions = optionSources.api.map((source) => ({
    value: source.url, // Use URL as the value for simplicity
    label: source.label,
  }));

  // const showValidationInputs = ["text", "number", "textarea"].includes(
  //   config.type
  // );
  const showValidationInputs = ["no-text"].includes(
    config.type
  );

  return (
    <div className="modal-overlay">
      <div
        className="modal-content-wrapper card-shadow-md"
        style={{ width: "450px" }}
      >
        <h3 className="mb-24">{isEditMode ? "Edit Field" : "Add Field"}</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-16">
            <label className="form-label fw-bold">Field Name</label>
            <CustomSelect
              options={fieldNameOptions}
              value={fieldNameOptions.find((opt) => opt.value === config.name)}
              onChange={(selectedOption) => {
                handleChange({
                  target: {
                    name: "name",
                    value: selectedOption?.value || "",
                  },
                });
                handleChange({
                  target: {
                    name: "label",
                    value: selectedOption?.label || "",
                  },
                });
              }}
              placeholder="-- Select Field Name --"
              isSearchable
            />
          </div>
          <div className="mb-16">
            <label className="form-label fw-bold">Field Label</label>
            <input
              type="text"
              className="form-control"
              name="label"
              value={config.label}
              onChange={handleChange}
              required
            />
          </div>
          {showValidationInputs && (
            <div className="mb-16">
              <label className="form-label fw-bold">Validation</label>
              <div className="row">
                <div className="col">
                  <input
                    type="number"
                    className="form-control"
                    name="min"
                    min={0}
                    value={config.min}
                    onChange={handleChange}
                    placeholder="Min Length/Value"
                  />
                </div>
                <div className="col">
                  <input
                    type="number"
                    className="form-control"
                    name="max"
                    value={config.max}
                    onChange={handleChange}
                    placeholder="Max Length/Value"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="mb-16 form-check d-flex align-items-center">
            <input
              type="checkbox"
              className="form-check-input"
              id="required-checkbox"
              name="required"
              checked={config.required}
              onChange={handleChange}
            />
            <label
              htmlFor="required-checkbox"
              className="form-check-label ms-2"
            >
              Required
            </label>
          </div>
          {config.type === "select" && (
            <div className="mb-16">
              <label className="form-label fw-bold">Options Source</label>
              <CustomSelect
                options={sourceTypeOptions}
                value={sourceTypeOptions.find(
                  (opt) => opt.value === optionSourceType
                )}
                onChange={handleSourceTypeChange}
                placeholder="-- Select Source Type --"
              />
              {optionSourceType === "hard_coded" && (
                <div className="mt-16">
                  <label className="form-label fw-bold">
                    Dropdown Options (pipe-separated)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="options"
                    value={config.options}
                    onChange={handleChange}
                    placeholder="e.g., option1|option2|option3"
                  />
                </div>
              )}
              {optionSourceType === "api" && (
                <div
                  className="mt-16"
                  style={{ marginBottom: isSelectOpen ? "250px" : "" }}
                >
                  <label className="form-label fw-bold">
                    Select API Source
                  </label>
                  <CustomSelect
                    options={apiSourceOptions}
                    value={
                      apiSourceOptions.find(
                        (opt) => opt.value === selectedApiSource?.url
                      ) || null
                    }
                    onChange={(selected) => {
                      setSelectedApiSource(
                        selected
                          ? optionSources.api.find(
                              (source) => source.url === selected.value
                            )
                          : null
                      );
                    }}
                    placeholder="-- Select API --"
                    isClearable
                    onMenuOpen={() => setIsSelectOpen(true)} // ✅ Yahan use karo
                    onMenuClose={() => setIsSelectOpen(false)} // ✅ Yahan use karo
                  />
                </div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-24">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary-600">
              {isEditMode ? "Update Field" : "Add Field"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
// Custom Node Component
const CustomNode = memo(({ id, data }) => {
  const handleDragStart = (event, fieldId) => {
    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({ fieldId, nodeId: id })
    );
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const rawData = event.dataTransfer.getData("application/json");
    if (!rawData) {
      console.warn("No data found in drag-and-drop operation");
      return;
    }
    let draggedData;
    try {
      draggedData = JSON.parse(rawData);
    } catch (error) {
      console.error("Failed to parse dragged data:", error);
      handleCustomResponse("error", "Invalid drag-and-drop data");
      return;
    }
    const { fieldId: draggedFieldId, nodeId: sourceNodeId } = draggedData;

    if (sourceNodeId !== id) return;

    const targetFieldElement = event.target.closest("[data-field-id]");
    if (!targetFieldElement) return;

    const targetFieldId = targetFieldElement.dataset.fieldId;
    if (!draggedFieldId || !targetFieldId) return;

    const updatedFields = [...data.fields];
    const draggedIndex = updatedFields.findIndex(
      (f) => f.id === draggedFieldId
    );
    const targetIndex = updatedFields.findIndex((f) => f.id === targetFieldId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const [draggedField] = updatedFields.splice(draggedIndex, 1);
    updatedFields.splice(targetIndex, 0, draggedField);

    const reorderedFields = updatedFields.map((f, index) => ({
      ...f,
      order: index + 1,
    }));

    data.onUpdateFields(id, reorderedFields);
  };

  const handleMoveField = (fieldId, direction) => {
    const updatedFields = [...data.fields];
    const index = updatedFields.findIndex((f) => f.id === fieldId);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === updatedFields.length - 1)
    ) {
      return;
    }

    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [updatedFields[index], updatedFields[swapIndex]] = [
      updatedFields[swapIndex],
      updatedFields[index],
    ];

    const reorderedFields = updatedFields.map((f, i) => ({
      ...f,
      order: i + 1,
    }));

    data.onUpdateFields(id, reorderedFields);
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        padding: "0",
        border: "2px solid #e5e7eb",
        borderRadius: "16px",
        width: "320px",
        // height: "200px",
        boxShadow: "0 8px 25px rgba(0,0,0,0.08), 0 3px 10px rgba(0,0,0,0.04)",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow =
          "0 12px 35px rgba(0,0,0,0.12), 0 6px 15px rgba(0,0,0,0.06)";
        e.currentTarget.style.borderColor = "#cbd5e1";
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow =
          "0 8px 25px rgba(0,0,0,0.08), 0 3px 10px rgba(0,0,0,0.04)";
        e.currentTarget.style.borderColor = "#e5e7eb";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div
        style={{
          // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          background:
            "linear-gradient(135deg, #862509 0%, #f85930 50%, #f99e5f 100%)",
          padding: "16px 20px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 10px rgba(102, 126, 234, 0.2)",
          borderRadius: "14px 14px 0 0",
          flexShrink: 0,
          height: "60px",
        }}
      >
        <div
          style={{
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: "600",
            letterSpacing: "0.3px",
            textShadow: "0 1px 2px rgba(0,0,0,0.1)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: "6px",
              height: "6px",
              backgroundColor: "#ffffff",
              borderRadius: "50%",
              opacity: "0.8",
              flexShrink: 0,
            }}
          ></div>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data.label}
          </span>
        </div>
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          height: "calc(100% - 60px)",
          position: "relative",
        }}
        onScroll={(e) => {
          const scrollTop = e.target.scrollTop;
          const maxScroll = e.target.scrollHeight - e.target.clientHeight;
          const container = e.target.closest("div");
          if (container) {
            container.style.setProperty(
              "--scroll-top-opacity",
              scrollTop > 10 ? "1" : "0"
            );
            container.style.setProperty(
              "--scroll-bottom-opacity",
              scrollTop < maxScroll - 10 ? "1" : "0"
            );
          }
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "8px",
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.06) 0%, transparent 100%)",
            zIndex: 1,
            opacity: "var(--scroll-top-opacity, 0)",
            transition: "opacity 0.3s ease",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "8px",
            background:
              "linear-gradient(0deg, rgba(0,0,0,0.06) 0%, transparent 100%)",
            zIndex: 1,
            opacity: "var(--scroll-bottom-opacity, 0)",
            transition: "opacity 0.3s ease",
            pointerEvents: "none",
          }}
        />
        {data.fields?.length > 0 ? (
          <div
            style={{
              padding: "16px 12px 16px 16px",
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              position: "relative",
              scrollbarWidth: "thin",
              scrollbarColor: "#cbd5e1 #f8fafc",
            }}
          >
            <style>
              {`
                div[style*="overflow-y: auto"]::-webkit-scrollbar {
                  width: 6px;
                }
                div[style*="overflow-y: auto"]::-webkit-scrollbar-track {
                  background: #f8fafc;
                  border-radius: 6px;
                  margin: 4px 0;
                }
                div[style*="overflow-y: auto"]::-webkit-scrollbar-thumb {
                  background: linear-gradient(180deg, #cbd5e1 0%, #94a3b8 100%);
                  border-radius: 6px;
                  border: 1px solid #f8fafc;
                }
                div[style*="overflow-y: auto"]::-webkit-scrollbar-thumb:hover {
                  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
                }
                div[style*="overflow-y: auto"]::-webkit-scrollbar-thumb:active {
                  background: linear-gradient(180deg, #64748b 0%, #475569 100%);
                }
                div[style*="overflow-y: auto"]::-webkit-scrollbar-corner {
                  background: #f8fafc;
                }
              `}
            </style>
            {data.fields
              .sort((a, b) => a.order - b.order)
              .map((field, index) => (
                <div
                  key={field.id}
                  data-field-id={field.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, field.id)}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                    border: "1px solid #e2e8f0",
                    padding: "12px 14px",
                    borderRadius: "10px",
                    fontSize: "12px",
                    color: "#475569",
                    cursor: "move",
                    transition: "all 0.2s ease",
                    position: "relative",
                    minHeight: "44px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(0,0,0,0.08)";
                    e.currentTarget.style.borderColor = "#cbd5e1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 1px 3px rgba(0,0,0,0.05)";
                    e.currentTarget.style.borderColor = "#e2e8f0";
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "6px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                      opacity: "0.4",
                    }}
                  >
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        style={{
                          width: "3px",
                          height: "3px",
                          backgroundColor: "#94a3b8",
                          borderRadius: "50%",
                        }}
                      ></div>
                    ))}
                  </div>
                  <span
                    style={{
                      fontWeight: "500",
                      fontSize: "12px",
                      color: "#1e293b",
                      marginLeft: "18px",
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        width: "4px",
                        height: "4px",
                        backgroundColor: "#3b82f6",
                        borderRadius: "50%",
                        opacity: "0.7",
                        flexShrink: 0,
                      }}
                    ></div>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {field.label || field.name}
                    </span>
                    {field.required && (
                      <span
                        style={{
                          color: "#ef4444",
                          fontWeight: "600",
                          fontSize: "12px",
                          flexShrink: 0,
                        }}
                      >
                        *
                      </span>
                    )}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      gap: "2px",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <button
                      onClick={() => handleMoveField(field.id, "up")}
                      disabled={index === 0}
                      style={{
                        background: "none",
                        border: "none",
                        color: index === 0 ? "#cbd5e1" : "#64748b",
                        cursor: index === 0 ? "not-allowed" : "pointer",
                        padding: "2px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        opacity: index === 0 ? "0.5" : "1",
                        width: "20px",
                        height: "20px",
                      }}
                      title="Move Up"
                      onMouseEnter={(e) => {
                        if (index !== 0) {
                          e.currentTarget.style.backgroundColor = "#f1f5f9";
                          e.currentTarget.style.color = "#3b82f6";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color =
                          index === 0 ? "#cbd5e1" : "#64748b";
                      }}
                    >
                      <ChevronUp size={10} />
                    </button>
                    <button
                      onClick={() => handleMoveField(field.id, "down")}
                      disabled={index === data.fields.length - 1}
                      style={{
                        background: "none",
                        border: "none",
                        color:
                          index === data.fields.length - 1
                            ? "#cbd5e1"
                            : "#64748b",
                        cursor:
                          index === data.fields.length - 1
                            ? "not-allowed"
                            : "pointer",
                        padding: "2px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        opacity: index === data.fields.length - 1 ? "0.5" : "1",
                        width: "20px",
                        height: "20px",
                      }}
                      title="Move Down"
                      onMouseEnter={(e) => {
                        if (index !== data.fields.length - 1) {
                          e.currentTarget.style.backgroundColor = "#f1f5f9";
                          e.currentTarget.style.color = "#3b82f6";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color =
                          index === data.fields.length - 1
                            ? "#cbd5e1"
                            : "#64748b";
                      }}
                    >
                      <ChevronDownMove size={10} />
                    </button>
                    <button
                      onClick={() => data.onEditField(field, id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#64748b",
                        cursor: "pointer",
                        padding: "2px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        width: "20px",
                        height: "20px",
                      }}
                      title="Edit Field"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f1f5f9";
                        e.currentTarget.style.color = "#3b82f6";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#64748b";
                      }}
                    >
                      <Edit size={10} />
                    </button>
                    <button
                      onClick={() => data.onDeleteField(field.id, id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#64748b",
                        cursor: "pointer",
                        padding: "2px",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        width: "20px",
                        height: "20px",
                      }}
                      title="Delete Field"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fef2f2";
                        e.currentTarget.style.color = "#ef4444";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#64748b";
                      }}
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              color: "#9ca3af",
              fontSize: "11px",
              textAlign: "center",
              background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
              borderRadius: "8px",
              border: "1px dashed #d1d5db",
              margin: "16px",
              flex: 1,
            }}
          >
            <div
              style={{
                width: "32px",
                height: "32px",
                background: "linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "8px",
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  background:
                    "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)",
                  borderRadius: "2px",
                  opacity: "0.6",
                }}
              ></div>
            </div>
            <div
              style={{
                fontWeight: "500",
                marginBottom: "2px",
                color: "#6b7280",
                fontSize: "11px",
              }}
            >
              No fields yet
            </div>
            <div
              style={{
                fontSize: "10px",
                color: "#9ca3af",
              }}
            >
              Add fields to get started
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

// Main WorkFLowsSuggestionEditLayer Component
const WorkFLowsSuggestionEditLayer = () => {
  const navigate = useNavigate();
  const { ID } = useParams(); // Get ID from URL parameters
  const id = ID;
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesState] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [fieldConfigModal, setFieldConfigModal] = useState({
    isOpen: false,
    field: null,
    isEditMode: false,
    nodeId: null,
  });
  const [optionSources, setOptionSources] = useState({ master: [], api: [] });
  const [fieldNames, setFieldNames] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const saveToHistory = useCallback(
    (newNodes, newEdges) => {
      setHistory((prev) => {
        const newHistory = prev.slice(0, historyIndex + 1);
        return [
          ...newHistory,
          {
            nodes: JSON.parse(JSON.stringify(newNodes)),
            edges: JSON.parse(JSON.stringify(newEdges)),
          },
        ];
      });
      setHistoryIndex((prev) => prev + 1);
    },
    [historyIndex]
  );

  const nodeTypes = {
    custom: (props) => (
      <CustomNode
        {...props}
        data={{
          ...props.data,
          onUpdateFields: handleUpdateFields,
          onEditField: handleEditField,
          onDeleteField: handleDeleteField,
        }}
      />
    ),
  };

  const fetchFields = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiCall(
        "get",
        `/workflow/template/fields/${id}`,
        null,
        navigate
      );
      if (res.data.success) {
        const fieldsData = res.data.data;
        const newNodes = [
          {
            id: "template-node",
            type: "custom",
            position: { x: 100, y: 100 },
            data: {
              template_id: id,
              label:
                fieldsData.template.template_label || "Template Fields check",
              fields:
                fieldsData.fields?.map((field, idx) => ({
                  id: String(field.id || `client-${Date.now()}-${idx}`),
                  field_id: field.id || null,
                  name: field.name,
                  label: field.label,
                  type: field.type,
                  required: field.required,
                  validation: field.validation,
                  options:
                    field.options && field.options !== "null"
                      ? field.options
                      : null,
                  options_source: field.options_source,
                  options_label_field: field.options_label_field,
                  options_value_field: field.options_value_field,
                  order: field.order || idx + 1,
                })) || [],
            },
          },
        ];

        setNodes(newNodes);
        setEdges([]); // No edges since we have a single node
        saveToHistory(newNodes, []);

        const handleRes = res.data;
        handleApiResponse(handleRes, "success", "GET");
      } else {
        throw new Error(res.data.message || "Failed to fetch fields");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
    } finally {
      setIsLoading(false);
    }
  }, [navigate, setNodes, setEdges, saveToHistory, id]);

  const fetchOptionSources = useCallback(async () => {
    try {
      const [masterRes, apiRes, fieldNamesRes] = await Promise.all([
        apiCall("get", "/get-option-source-data/1", null, navigate),
        apiCall("get", "/get-option-source-data/2", null, navigate),
        apiCall("get", "/workflow-field-names", null, navigate),
      ]);
      setOptionSources({
        master: masterRes.data.data,
        api: apiRes.data.data,
      });
      setFieldNames(fieldNamesRes.data.data);
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "GET");
    }
  }, [navigate]);

  useEffect(() => {
    fetchFields();
    fetchOptionSources();
  }, [fetchOptionSources]);

  const handleSaveFieldConfig = (updatedField) => {
    const { nodeId } = fieldConfigModal;
    setNodes((nds) => {
      const updatedNodes = nds.map((node) => {
        if (node.id === nodeId) {
          const isDuplicate = node.data.fields.some(
            (f) => f.name === updatedField.name && f.id !== updatedField.id
          );
          if (isDuplicate) {
            handleCustomResponse(
              "error",
              "Field name already exists in this template."
            );
            return node;
          }

          const newFields = fieldConfigModal.isEditMode
            ? node.data.fields.map((f) =>
                f.id === updatedField.id
                  ? { ...updatedField, order: f.order }
                  : f
              )
            : [
                ...node.data.fields,
                {
                  ...updatedField,
                  id: `new-${uuidv4()}`,
                  field_id: null,
                  order: node.data.fields.length + 1,
                },
              ];

          const sortedFields = newFields
            .sort((a, b) => a.order - b.order)
            .map((f, index) => ({ ...f, order: index + 1 }));

          return {
            ...node,
            data: { ...node.data, fields: sortedFields },
          };
        }
        return node;
      });
      saveToHistory(updatedNodes, edges);
      return updatedNodes;
    });
    const msg = fieldConfigModal.isEditMode ? "Field updated" : "Field added";
    handleCustomResponse("success", msg);
  };

  const handleEditField = (field, nodeId) => {
    setFieldConfigModal({
      isOpen: true,
      field: field,
      isEditMode: true,
      nodeId: nodeId,
    });
  };

  const handleDeleteField = useCallback(
    (fieldId, nodeId) => {
      setNodes((nds) => {
        const updatedNodes = nds.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  fields: node.data.fields
                    .filter((f) => f.id !== fieldId)
                    .map((f, index) => ({
                      ...f,
                      order: index + 1,
                    })),
                },
              }
            : node
        );
        saveToHistory(updatedNodes, edges);
        return updatedNodes;
      });
      handleCustomResponse("success", "Field removed successfully");
    },
    [setNodes, edges, saveToHistory]
  );

  const handleUpdateFields = useCallback(
    (nodeId, reorderedFields) => {
      const sortedFields = reorderedFields.map((field, index) => ({
        ...field,
        order: index + 1,
      }));
      setNodes((nds) => {
        const updatedNodes = nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: { ...node.data, fields: sortedFields },
            };
          }
          return node;
        });
        saveToHistory(updatedNodes, edges);
        return updatedNodes;
      });
      handleCustomResponse("success", "Fields reordered within template.");
    },
    [setNodes, edges, saveToHistory]
  );

  const handleSaveFields = async () => {
    setSaveStatus("saving");
    setIsLoading(true);
    try {
      const templateNode = nodes.find((node) => node.id === "template-node");
      if (!templateNode) {
        throw new Error("Template node not found");
      }
      const fieldsData = {
        fields: templateNode.data.fields
          .sort((a, b) => a.order - b.order)
          .map((field, index) => ({
            field_id: field.field_id || null,
            name: field.name,
            label: field.label,
            type: field.type,
            required: field.required,
            validation: field.validation,
            options: field.options || {},
            options_source: field.options_source || null,
            options_label_field: field.options_label_field || null,
            options_value_field: field.options_value_field || null,
            order: index + 1,
          })),
      };

      const res = await apiCall(
        "post",
        `/workflow/template/add-fields/${id}`,
        fieldsData,
        navigate
      );
      if (res.data.success) {
        const handleRes = res.data;
        handleApiResponse(handleRes, "success", "POST");
        setSaveStatus("success");
        setNodes((prevNodes) =>
          prevNodes.map((node) => {
            if (node.id === "template-node") {
              return {
                ...node,
                data: {
                  ...node.data,
                  fields: node.data.fields.map((field) => {
                    const savedField = res.data.fields?.find(
                      (f) =>
                        f.tempId === field.id || f.field_id === field.field_id
                    );
                    if (savedField) {
                      return {
                        ...field,
                        id: String(savedField.field_id),
                        field_id: savedField.field_id,
                        order: savedField.order,
                      };
                    }
                    return field;
                  }),
                },
              };
            }
            return node;
          })
        );
        await fetchFields();
      } else {
        throw new Error(res.data.message || "Failed to save fields");
      }
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
      setSaveStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  const onDragStart = (event, component) => {
    event.dataTransfer.setData(
      "application/reactflow",
      JSON.stringify(component)
    );
    event.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (!reactFlowInstance || !selectedNode) {
        handleCustomResponse(
          "error",
          "Please select the template to add fields to."
        );
        return;
      }

      const component = JSON.parse(
        event.dataTransfer.getData("application/reactflow")
      );
      const nextFieldOrder = selectedNode.data.fields.length + 1;

      setFieldConfigModal({
        isOpen: true,
        field: { ...component, order: nextFieldOrder },
        isEditMode: false,
        nodeId: selectedNode.id,
      });
      handleCustomResponse("info", `Configuring new ${component.type} field.`);
    },
    [reactFlowInstance, selectedNode]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex((prev) => prev - 1);
      const previousState = history[historyIndex - 1];
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      handleCustomResponse("info", "Action undone");
    } else {
      handleCustomResponse("info", "No more actions to undo");
    }
  }, [history, historyIndex, setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex((prev) => prev + 1);
      const nextState = history[historyIndex + 1];
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      handleCustomResponse("info", "Action redone");
    } else {
      handleCustomResponse("info", "No more actions to redo");
    }
  }, [history, historyIndex, setNodes, setEdges]);

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <>
      {/* Mobile/Tablet Compatibility Message */}
      <div className="d-lg-none text-center p-4 min-vh-100 d-flex flex-column justify-content-center align-items-center bg-neutral-50">
        <div className="max-w-md">
          <h3 className="text-2xl font-semibold text-neutral-800 mb-4">
            Device Compatibility
          </h3>
          <p className="text-neutral-600 mb-6 p-5">
            This workflow editor is optimized for larger screens (large devices
            and up). Please use a desktop or larger tablet for the best
            experience.
          </p>
          <button
            className="btn btn-primary-600 px-18 py-18 rounded-md transition-colors duration-200 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={() => navigate(-1)}
            aria-label="Go back to previous page"
          >
            <span className="d-flex align-items-center justify-content-center gap-2">
              <ArrowLeft size={18} />
              Go Back
            </span>
          </button>
        </div>
      </div>

      {/* Main Editor (Visible only on large screens) */}
      <div className="d-none d-lg-block">
        <div
          className="container-fluid"
          style={{ height: "100vh", display: "flex", flexDirection: "column" }}
        >
          <div className="row p-3 shadow-sm bg-white" style={{ flexShrink: 0 }}>
            <div className="col-6 d-flex align-items-center">
              <button
                className="btn btn-secondary"
                onClick={() =>
                  navigate("/workflows-suggestions", { replace: true })
                }
              >
                <div className="d-flex align-items-center gap-2">
                  <Navigation size={16} />
                  <span>Back</span>
                </div>
              </button>
            </div>
            <div className="col-6 text-end">
              <button
                className="btn btn-outline-secondary me-2"
                onClick={handleUndo}
                disabled={historyIndex <= 0}
                title="Undo"
                style={{ padding: "6px 12px" }}
              >
                <Undo size={16} />
              </button>
              <button
                className="btn btn-outline-secondary me-2"
                onClick={handleRedo}
                disabled={historyIndex >= history.length - 1}
                title="Redo"
                style={{ padding: "6px 12px" }}
              >
                <Redo size={16} />
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveFields}
                disabled={isLoading || saveStatus === "saving"}
              >
                <div className="d-flex align-items-center gap-1">
                  <Save size={16} style={{ marginRight: "8px" }} />
                  {isLoading || saveStatus === "saving"
                    ? "Saving..."
                    : "Save Fields"}
                </div>
              </button>
            </div>
          </div>
          <div className="row" style={{ flex: 1, minHeight: 0 }}>
            <div
              className="col-md-2 border-end p-3 bg-white"
              style={{ boxShadow: "0 2px 5px rgba(0,0,0,0.1)", overflowY: "auto" }}
            >
              <span
                style={{
                  color: "#ff0000",
                  fontWeight: "bold",
                  fontSize: "16px",
                  display: "block",
                  marginBottom: "10px",
                }}
              >
                Available Components
              </span>
              {selectedNode ? (
                <div>
                  <p className="small">
                    Drag a field onto the selected template:{" "}
                    <strong>{selectedNode.data.label}</strong>
                  </p>
                  <div className="overflow-auto">
                    {htmlComponents.map((component) => {
                      const IconComponent = component.icon;
                      return (
                        <button
                          key={component.step_field_id}
                          className="btn btn-outline-secondary w-100 mb-2"
                          draggable
                          onDragStart={(event) => onDragStart(event, component)}
                          style={{ cursor: "grab", textAlign: "left" }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <IconComponent size={16} />
                            <span className="text-sm small">
                              {component.displayName}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="small">Select a template to add fields.</p>
                  <div className="overflow-auto">
                    {htmlComponents.map((component) => {
                      const IconComponent = component.icon;
                      return (
                        <button
                          key={component.step_field_id}
                          className="btn btn-outline-secondary w-100 mb-2"
                          draggable
                          style={{ cursor: "grab", textAlign: "left" }}
                          disabled
                        >
                          <div className="d-flex align-items-center gap-2">
                            <IconComponent size={16} />
                            <span className="text-sm small">
                              {component.displayName}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div
              className="col-md-10"
              ref={reactFlowWrapper}
              style={{ height: "100%" }}
            >
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesState}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                fitView
              >
                <MiniMap
                  style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                  }}
                />
                <Controls
                  style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                  }}
                />
                <Background color="#ccc" gap={16} />
              </ReactFlow>
            </div>
          </div>
          <FieldConfigModal
            isOpen={fieldConfigModal.isOpen}
            onClose={() => {
              setFieldConfigModal({
                isOpen: false,
                field: null,
                isEditMode: false,
                nodeId: null,
              });
              if (fieldConfigModal.field && !fieldConfigModal.isEditMode) {
                setNodes((nds) =>
                  nds.map((node) =>
                    node.id === fieldConfigModal.nodeId
                      ? {
                          ...node,
                          data: {
                            ...node.data,
                            fields: node.data.fields
                              .filter((f) => f.id !== fieldConfigModal.field.id)
                              .map((f, index) => ({ ...f, order: index + 1 })),
                          },
                        }
                      : node
                  )
                );
                saveToHistory(nodes, edges);
                handleCustomResponse("info", "New field discarded.");
              }
            }}
            field={fieldConfigModal.field}
            onSave={handleSaveFieldConfig}
            isEditMode={fieldConfigModal.isEditMode}
            fieldNames={fieldNames}
            optionSources={optionSources}
          />
        </div>
      </div>
    </>
  );
};

export default WorkFLowsSuggestionEditLayer;